import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACCESSIBLE_CLIENTS } from '@src/graphql/queries/accessibleClients';
import { AccessType } from '@src/types';
/**
 * Hook to fetch the list of clients accessible to the current user.
 *
 * Works for authenticated users to:
 * 1. Populate the SelectClient page after login
 * 2. Enable client switching within the same employer
 *
 * @returns Object containing:
 *   - `clients`: Array of all AccessibleClient objects the user can access
 *   - `employeeClients`: Array of clients where user has employee access (eligible for reimbursement), sorted by name
 *   - `approverClients`: Array of clients where user has approver access, sorted by name
 *   - `loading`: Boolean indicating if the query is in progress
 *   - `error`: ApolloError if the query failed, undefined otherwise
 *
 * @example
 * const { employeeClients, approverClients, loading, error } = useAccessibleClients();
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage />;
 *
 * return (
 *   <>
 *     {employeeClients.map(client => <EmployeeCard key={client.id} client={client} />)}
 *     {approverClients.map(client => <ApproverCard key={client.id} client={client} />)}
 *   </>
 * );
 */
export const useAccessibleClients = () => {
    const { data, loading, error } = useQuery(GET_ACCESSIBLE_CLIENTS);
    const clients = useMemo(() => (data?.accessibleClients || []), [data?.accessibleClients]);
    // Separate clients into employee clients (eligible for reimbursement) and approver clients
    const employeeClients = useMemo(() => clients
        .filter(client => client.accessType === AccessType.EMPLOYEE)
        .sort((a, b) => a.name.localeCompare(b.name)), [clients]);
    const approverClients = useMemo(() => clients
        .filter(client => client.accessType === AccessType.APPROVER)
        .sort((a, b) => a.name.localeCompare(b.name)), [clients]);
    return {
        clients,
        employeeClients,
        approverClients,
        loading,
        error,
    };
};
